import { Component, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { Template } from '../pages/generator/email.store';
import { GeneratorService, StripoTemplate, StripoListResponse } from 'src/app/deebr/service/generator.service';

interface FilterOption {
  name: string;
  count: number;
}

@Component({
    selector: 'app-create-email-from-template',
    templateUrl: './create-email-from-template.component.html',
    styleUrls: ['./create-email-from-template.component.scss'],
    standalone: false
})
export class CreateEmailFromTemplateComponent {

  @Output() templateSelected = new EventEmitter();
  templates: Template[] = [];
  stripoBasicTemplates: StripoTemplate[] = [];

  // Onglet Inspiration : catalogue complet (gratuits + premium) chargé en une fois,
  // l'API Stripo n'ayant aucune recherche serveur le filtrage se fait ici.
  stripoInspirationTemplates: StripoTemplate[] = [];
  filteredInspirationTemplates: StripoTemplate[] = [];
  industryOptions: FilterOption[] = [];
  typeOptions: FilterOption[] = [];
  seasonOptions: FilterOption[] = [];

  isOpen = false;
  searchQuery: string = '';
  selectedIndustry: string = '';
  selectedType: string = '';
  selectedSeason: string = '';
  selectedTab: 'mytemplates' | 'basique' | 'inspiration' = 'mytemplates';
  loadingtemplates = false;
  catalogError = false;

  // Rendu progressif : on n'affiche pas les 1700 vignettes d'un coup
  private static readonly PAGE_RENDER_SIZE = 60;
  visibleCount = CreateEmailFromTemplateComponent.PAGE_RENDER_SIZE;

  constructor(
    private cdRef: ChangeDetectorRef,
    private generatorService: GeneratorService
  ) {
    this.generatorService.getTemplates().subscribe((templates: Template[]) => {
      this.templates = templates;
      this.cdRef.detectChanges();
    });
    this.generatorService.getStripoTemplates('BASIC', 'ACTUAL', 50).subscribe((response: StripoListResponse) => {
      this.stripoBasicTemplates = response.data;
    });
  }

  private loadInspirationCatalog() {
    if (this.loadingtemplates || this.stripoInspirationTemplates.length) {
      return;
    }
    this.loadingtemplates = true;
    this.catalogError = false;
    this.generatorService.getStripoInspirationCatalog().subscribe({
      next: (res: StripoListResponse) => {
        this.stripoInspirationTemplates = res.data;
        this.buildFilterOptions();
        this.applyInspirationFilters();
        this.loadingtemplates = false;
        this.cdRef.detectChanges();
      },
      error: () => {
        this.loadingtemplates = false;
        this.catalogError = true;
        this.cdRef.detectChanges();
      }
    });
  }

  retryCatalog() {
    this.stripoInspirationTemplates = [];
    this.loadInspirationCatalog();
  }

  private buildFilterOptions() {
    this.industryOptions = this.countTags('templateIndustries');
    this.typeOptions = this.countTags('templateTypes');
    this.seasonOptions = this.countTags('templateSeasons');
  }

  // Les valeurs les plus représentées en premier : Ecommerce, Fashion, Christmas, Black Friday...
  private countTags(field: 'templateIndustries' | 'templateTypes' | 'templateSeasons'): FilterOption[] {
    const counts = new Map<string, number>();
    for (const template of this.stripoInspirationTemplates) {
      for (const tag of template[field] ?? []) {
        counts.set(tag.name, (counts.get(tag.name) ?? 0) + 1);
      }
    }
    return Array.from(counts, ([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }

  applyInspirationFilters() {
    const query = this.searchQuery?.toLowerCase().trim() ?? '';

    this.filteredInspirationTemplates = this.stripoInspirationTemplates.filter(template => {
      if (query && !this.matchesQuery(template, query)) { return false; }
      if (this.selectedIndustry && !this.hasTag(template.templateIndustries, this.selectedIndustry)) { return false; }
      if (this.selectedType && !this.hasTag(template.templateTypes, this.selectedType)) { return false; }
      if (this.selectedSeason && !this.hasTag(template.templateSeasons, this.selectedSeason)) { return false; }
      return true;
    });

    this.visibleCount = CreateEmailFromTemplateComponent.PAGE_RENDER_SIZE;
  }

  // Les noms Stripo sont peu descriptifs ("Mind summit"), on cherche aussi dans les tags
  private matchesQuery(template: StripoTemplate, query: string): boolean {
    if (template.name?.toLowerCase().includes(query)) { return true; }
    const tags = [
      ...(template.templateIndustries ?? []),
      ...(template.templateTypes ?? []),
      ...(template.templateSeasons ?? []),
      ...(template.templateFeatures ?? []),
    ];
    return tags.some(tag => tag.name.toLowerCase().includes(query));
  }

  private hasTag(tags: { id: number; name: string }[] | undefined, name: string): boolean {
    return (tags ?? []).some(tag => tag.name === name);
  }

  resetFilters() {
    this.searchQuery = '';
    this.selectedIndustry = '';
    this.selectedType = '';
    this.selectedSeason = '';
    this.applyInspirationFilters();
  }

  get hasActiveFilters(): boolean {
    return !!(this.searchQuery?.trim() || this.selectedIndustry || this.selectedType || this.selectedSeason);
  }

  get visibleInspirationTemplates(): StripoTemplate[] {
    return this.filteredInspirationTemplates.slice(0, this.visibleCount);
  }

  onScroll(event: any) {
    if (this.selectedTab !== 'inspiration') {
      return;
    }
    if (this.visibleCount >= this.filteredInspirationTemplates.length) {
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = event.target;
    if (scrollTop + clientHeight >= scrollHeight - 200) {
      this.visibleCount += CreateEmailFromTemplateComponent.PAGE_RENDER_SIZE;
    }
  }

  onSearchChange() {
    if (this.selectedTab === 'inspiration') {
      this.applyInspirationFilters();
    }
  }

  get filteredTemplates(): Template[] {
    // Start with either the full list or the filtered list
    const list = this.searchQuery?.trim()
      ? this.templates.filter(template =>
          template.name?.toLowerCase().includes(this.searchQuery.toLowerCase().trim())
        )
      : [...this.templates];  // clone to avoid mutating the original array

    // Sort by name, ascending
    return list.sort((a, b) =>
      (a.name ?? '').localeCompare(b.name ?? '')
    );
  }

  get filteredStripoBasicTemplates(): StripoTemplate[] {
    if (!this.searchQuery || this.searchQuery.trim() === '') {
      return this.stripoBasicTemplates;
    }
    const query = this.searchQuery.toLowerCase().trim();
    return this.stripoBasicTemplates.filter(template =>
      template.name?.toLowerCase().includes(query)
    );
  }

  selectTab(tab: 'mytemplates' | 'basique' | 'inspiration') {
    this.selectedTab = tab;
    if (tab === 'inspiration') {
      this.loadInspirationCatalog();
    }
  }

  onTemplateModalSelect(event: { id: number; name: string; type?: string }) {
    this.templateSelected.emit({ id: event.id, name: event.name, type: event.type ?? 'mytemplates' });
    this.isOpen = false;
  }

  selectTemplate(template: Template) {
    this.templateSelected.emit({ id: template.id, name: template.name, type: this.selectedTab });
    this.isOpen = false;
  }

  selectStripoTemplate(template: StripoTemplate) {
    this.templateSelected.emit({ id: template.templateId, name: template.name, type: this.selectedTab });
    this.isOpen = false;
  }

  open() {
    this.isOpen = true;
    this.selectedTab = 'mytemplates';
    this.resetFilters();
    // Préchargé dès l'ouverture pour que l'onglet Inspiration soit prêt au clic
    this.loadInspirationCatalog();
    this.cdRef.detectChanges();
  }

  close() {
    this.isOpen = false;
  }
}
